import "./Sider.scss";

import React, { useEffect, useState } from "react";

import { Layout, Menu } from "antd";
import PropTypes from "prop-types";
import { useHistory, useLocation } from "react-router-dom";

import {
  BarChartOutlined,
  BulbOutlined,
  DollarCircleOutlined,
  HomeOutlined,
  MessageOutlined,
  SettingOutlined,
  ShareAltOutlined,
  ShoppingCartOutlined,
  TeamOutlined,
  ToolOutlined,
  UserOutlined,
} from "@ant-design/icons";

import MenuSearch from "../../Components/MenuSearch/MenuSearch";
import {
  getParentMenuKey,
  getSliderKeyByRoute,
  MENU_STRUCTURE,
} from "../../routeConstants";

const Sider = ({
  // eslint-disable-next-line no-unused-vars
  setSliderValue,
  sliderValue,
  isRestricted,
  allowedFunctions,
}) => {
  const history = useHistory();
  const location = useLocation();

  // Get current selected key based on route
  const currentSliderValue =
    getSliderKeyByRoute(location.pathname) || sliderValue;

  // State for controlling which submenus are open
  const [openKeys, setOpenKeys] = useState([]);

  // Icon mapping
  const iconMap = {
    UserOutlined: <UserOutlined />,
    TeamOutlined: <TeamOutlined />,
    ShoppingCartOutlined: <ShoppingCartOutlined />,
    DollarCircleOutlined: <DollarCircleOutlined />,
    ShareAltOutlined: <ShareAltOutlined />,
    BulbOutlined: <BulbOutlined />,
    MessageOutlined: <MessageOutlined />,
    SettingOutlined: <SettingOutlined />,
    ToolOutlined: <ToolOutlined />,
    HomeOutlined: <HomeOutlined />,
    BarChartOutlined: <BarChartOutlined />,
  };

  // Set initial open keys based on current selected item
  useEffect(() => {
    const parentKey = getParentMenuKey(currentSliderValue);
    if (parentKey) {
      setOpenKeys((prevKeys) => {
        if (!prevKeys.includes(parentKey)) {
          return [...prevKeys, parentKey];
        }
        return prevKeys;
      });
    }
  }, [currentSliderValue]);

  const handleClick = (data) => {
    // Find the clicked item in the menu structure
    const selectedItem = MENU_STRUCTURE.filter(
      (section) => section.type === "group" || section.type === "submenu"
    )
      .flatMap((section) => section.items)
      .find((menuItem) => menuItem.key === data?.key);

    if (selectedItem) {
      history.push(selectedItem.route);
    }
  };

  const handleOpenChange = (keys) => {
    setOpenKeys(keys);
  };

  // Handle navigation from search
  const handleSearchNavigation = (selectedItem) => {
    // Open the parent submenu if the selected item is in a submenu
    const parentKey = getParentMenuKey(selectedItem.key);
    if (parentKey && !openKeys.includes(parentKey)) {
      setOpenKeys((prevKeys) => [...prevKeys, parentKey]);
    }
  };

  // Filter function to check if user has access to a menu item
  const hasAccess = (itemKey) => {
    return isRestricted ? allowedFunctions?.includes(itemKey) : true;
  };

  // Filter function to check if any items in a submenu are accessible
  const hasAccessToSubmenu = (items) => {
    return items.some((item) => hasAccess(item.key));
  };

  // Render menu items based on the menu structure
  const renderMenuItems = () => {
    return MENU_STRUCTURE.map((section) => {
      if (section.type === "group") {
        // Render group items as regular menu items
        const accessibleItems = section.items.filter((item) =>
          hasAccess(item.key)
        );

        if (accessibleItems.length === 0) return null;

        return accessibleItems.map((item) => (
          <Menu.Item key={item.key} icon={<HomeOutlined />}>
            {item.name}
          </Menu.Item>
        ));
      }

      if (section.type === "submenu") {
        // Only render submenu if user has access to at least one item
        if (!hasAccessToSubmenu(section.items)) return null;

        const accessibleItems = section.items.filter((item) =>
          hasAccess(item.key)
        );

        return (
          <Menu.SubMenu
            key={section.key}
            icon={iconMap[section.icon] || <SettingOutlined />}
            title={section.label}
          >
            {accessibleItems.map((item) => (
              <Menu.Item key={item.key}>{item.name}</Menu.Item>
            ))}
          </Menu.SubMenu>
        );
      }

      return null;
    })
      .filter(Boolean)
      .flat();
  };

  return (
    <Layout.Sider className="siteSidebar" width={280}>
      <div className="siteLogo">
        <img
          src="https://tagmango.com/staticassets/tagmango_typeface-%281%29.svg-1622023999423.svg"
          alt="TagMango Logo"
          width={145}
        />
      </div>
      <MenuSearch
        isRestricted={isRestricted}
        allowedFunctions={allowedFunctions}
        onNavigate={handleSearchNavigation}
      />
      <Menu
        theme="light"
        mode="inline"
        onClick={handleClick}
        selectedKeys={[currentSliderValue]}
        openKeys={openKeys}
        onOpenChange={handleOpenChange}
        style={{
          height: "100%",
          borderRight: 0,
        }}
      >
        {renderMenuItems()}
      </Menu>
    </Layout.Sider>
  );
};

Sider.propTypes = {
  setSliderValue: PropTypes.func.isRequired,
  sliderValue: PropTypes.string.isRequired,
  isRestricted: PropTypes.bool.isRequired,
  allowedFunctions: PropTypes.arrayOf(PropTypes.string).isRequired,
};

export default Sider;
